// src/tabs/logements/AddContactModal.jsx — PR-FIX-5c (ADR-163)
//
// Modale "Ajouter / Modifier contact personnalisé" Assistance.
//
// 3 modes :
//   - création (contact prop absent) → form vide + bouton "Ajouter"
//   - édition custom (contact prop présent, manual_id) → pré-rempli + "Enregistrer"
//   - édition IA (prop iaKey présente, BUG24/Option A) → pré-rempli, Type + Notes
//     masqués, phone optionnel, submit avec body.key (PAS contact_id).
//
// Pattern saisie libre uniquement (PAS de Google Places — D5 acté).
//
// a11y :
//   - role="dialog" + aria-modal="true" + aria-labelledby
//   - Esc handler (close)
//   - Focus trap léger via tabindex (le 1er input reçoit autofocus)
//   - minHeight 44 boutons (WCAG 2.5.5)
//
// Charte FBZ : navy #215387 + sky #87B8E6 + Playfair (titres) + Raleway (body).
// Inputs limités côté UI (sanitize miroir côté serveur lib/livret-sections-handlers.js).

import { useState, useEffect, useRef } from "react";
import { DS } from "../../lib/ui";

const CONTACT_TYPES = [
  { value: "veterinaire", label: "Vétérinaire" },
  { value: "dentiste", label: "Dentiste" },
  { value: "taxi", label: "Taxi" },
  { value: "babysitter", label: "Babysitter" },
  { value: "autre", label: "Autre" },
];

// Phone regex doit matcher /^[\d\s+\-().]{4,30}$/ côté serveur.
const PHONE_REGEX_CLIENT = /^[\d\s+\-().]{4,30}$/;

async function _apiCall(action, { method = "POST", body, headers } = {}) {
  const url = `/api/beds24-mapping?action=${action}`;
  const res = await fetch(url, {
    method,
    headers: { "Content-Type": "application/json", ...(headers || {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  const json = await res.json().catch(() => ({ error: "parse_fail" }));
  return { ok: res.ok, status: res.status, json };
}

export default function AddContactModal({
  logement_id,
  contact,
  iaKey = null,
  getAuthHeaders,
  onClose,
  onSaved,
}) {
  // Mode IA (BUG24/Option A) : édition d'un contact IA (medecin_proche /
  // pharmacie_garde / hopital_proche). Un IA n'a PAS de manual_id, donc on NE
  // dérive PAS isEdit du manual_id dans ce mode (sinon chemin création custom →
  // 400 type invalide côté serveur). Le mode IA force le chemin édition-IA.
  const isIaEdit = Boolean(iaKey);
  const isEdit = isIaEdit ? false : Boolean(contact && contact.manual_id);

  const [form, setForm] = useState({
    type: contact?.type || "veterinaire",
    name: contact?.name || "",
    phone: contact?.phone || "",
    address: contact?.address || "",
    notes: contact?.notes || "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const firstInputRef = useRef(null);

  // ── Esc handler + autofocus ───────────────────────────────────────────────
  useEffect(() => {
    function onEsc(e) {
      if (e.key === "Escape") onClose?.();
    }
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [onClose]);

  useEffect(() => {
    if (firstInputRef.current) {
      try {
        firstInputRef.current.focus();
      } catch (_e) {
        // pas critique
      }
    }
  }, []);

  // ── Validation client (mirror serveur) ────────────────────────────────────
  function _validate() {
    const name = form.name.trim();
    if (!name) return "Le nom est requis";
    if (name.length > 100) return "Nom trop long (max 100 caractères)";
    const phone = form.phone.trim();
    // Mode IA : phone optionnel (un contact IA peut ne pas avoir de tel —
    // Google Places sans numéro). Vide accepté = efface le tel.
    if (!isIaEdit && !phone) return "Le téléphone est requis";
    if (phone && !PHONE_REGEX_CLIENT.test(phone)) {
      return "Téléphone invalide (chiffres / espaces / + - ( ) . — 4 à 30 caractères)";
    }
    if (!isIaEdit && !CONTACT_TYPES.some((t) => t.value === form.type)) {
      return "Type invalide";
    }
    if ((form.address || "").length > 300) {
      return "Adresse trop longue (max 300 caractères)";
    }
    if (!isIaEdit && (form.notes || "").length > 300) {
      return "Notes trop longues (max 300 caractères)";
    }
    return null;
  }

  async function handleSubmit(e) {
    e?.preventDefault?.();
    const vErr = _validate();
    if (vErr) {
      setError(vErr);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const headers = getAuthHeaders ? await getAuthHeaders() : {};

      let result;
      if (isIaEdit) {
        // BUG24/Option A : édition IA → body.key (pas de contact_id, pas de
        // type ni notes). phone envoyé tel quel (vide = effacement serveur).
        const iaPayload = {
          name: form.name.trim(),
          phone: form.phone.trim(),
          address: form.address.trim(),
        };
        result = await _apiCall("livret_assistance_contact_update", {
          method: "PATCH",
          body: {
            logement_id,
            key: iaKey,
            contact: iaPayload,
          },
          headers,
        });
      } else {
        const payload = {
          type: form.type,
          name: form.name.trim(),
          phone: form.phone.trim(),
        };
        const address = form.address.trim();
        const notes = form.notes.trim();
        // ═══════════════════════════════════════════════════════════════════
        // 🔴 ON NE POUVAIT PAS EFFACER UNE ADRESSE — corrigé le 2026-08-05
        // ═══════════════════════════════════════════════════════════════════
        // Les champs vidés étaient OMIS du corps de la requête. Or la mise à
        // jour serveur est une FUSION partielle : « champ absent » y signifie
        // « ne touche pas », pas « efface ». Vider l'adresse d'un contact puis
        // enregistrer n'avait donc aucun effet — l'ancienne adresse revenait au
        // rechargement, sans le moindre message. Idem pour les notes.
        //
        // En ÉDITION, on envoie donc toujours les deux champs, même vides :
        // c'est exactement ce que fait déjà la branche « contact IA » du même
        // fichier, et le serveur sait le traiter (`typeof === "string"` →
        // écriture, y compris de la chaîne vide).
        //
        // En CRÉATION, rien ne change : il n'y a rien à effacer, on continue
        // d'omettre les champs vides.
        if (isEdit) {
          payload.address = address;
          payload.notes = notes;
        } else {
          if (address) payload.address = address;
          if (notes) payload.notes = notes;
        }

        if (isEdit) {
          result = await _apiCall("livret_assistance_contact_update", {
            method: "PATCH",
            body: {
              logement_id,
              contact_id: contact.manual_id,
              contact: payload,
            },
            headers,
          });
        } else {
          result = await _apiCall("livret_assistance_contact_add", {
            method: "POST",
            body: { logement_id, contact: payload },
            headers,
          });
        }
      }
      if (!result.ok) {
        setError(result.json.message || result.json.error || `HTTP ${result.status}`);
        return;
      }
      onSaved?.({
        key: isIaEdit ? iaKey : undefined,
        manual_id: isIaEdit ? undefined : result.json.manual_id || contact?.manual_id,
      });
      onClose?.();
    } catch (err) {
      setError(err.message || "Erreur réseau");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-contact-modal-title"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="fbz-modal-sheet"
        style={{
          background: DS.white,
          borderRadius: 12,
          padding: 20,
          maxWidth: 500,
          width: "100%",
          maxHeight: "90vh",
          overflow: "auto",
          fontFamily: DS.fontBody,
        }}
      >
        <h3
          id="add-contact-modal-title"
          style={{
            margin: 0,
            marginBottom: 14,
            fontFamily: DS.fontDisplay,
            color: DS.navy,
            fontSize: 18,
          }}
        >
          {isIaEdit
            ? "✏️ Modifier le contact (IA)"
            : isEdit
              ? "✏️ Modifier le contact"
              : "➕ Ajouter un contact"}
        </h3>

        <form onSubmit={handleSubmit}>
          {!isIaEdit && (
            <Field label="Type *">
              <select
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                style={_inputStyle()}
                ref={firstInputRef}
              >
                {CONTACT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </Field>
          )}

          <Field label="Nom *">
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              maxLength={100}
              placeholder="ex. Clinique Vétérinaire des Pyrénées"
              required
              style={_inputStyle()}
              aria-label="Nom du contact"
              ref={isIaEdit ? firstInputRef : undefined}
            />
          </Field>

          <Field label={isIaEdit ? "Téléphone (optionnel)" : "Téléphone *"}>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              maxLength={30}
              placeholder="+33 5 61 23 45 67"
              required={!isIaEdit}
              style={_inputStyle()}
              aria-label="Téléphone du contact"
            />
            <span style={_hintStyle()}>4-30 caractères, chiffres / espaces / + - ( ) .</span>
          </Field>

          <Field label="Adresse (optionnel)">
            <input
              type="text"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              maxLength={300}
              placeholder="10 rue Test, 31000 Toulouse"
              style={_inputStyle()}
              aria-label="Adresse du contact"
            />
          </Field>

          {!isIaEdit && (
            <Field label={`Notes (optionnel) — ${(form.notes || "").length}/300`}>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                maxLength={300}
                rows={3}
                placeholder="ex. Ouvert 24h/24, anglophone, etc."
                style={{ ..._inputStyle(), resize: "vertical", minHeight: 64 }}
                aria-label="Notes du contact"
              />
            </Field>
          )}

          {error && (
            <div role="alert" style={_errorBoxStyle()}>
              {error}
            </div>
          )}

          <div
            style={{
              marginTop: 16,
              display: "flex",
              gap: 8,
              justifyContent: "flex-end",
            }}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              style={_btnSecondaryStyle()}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={
                submitting ||
                !form.name.trim() ||
                (!isIaEdit && !form.phone.trim())
              }
              style={_btnPrimaryStyle(submitting)}
            >
              {submitting
                ? "⏳ Envoi…"
                : isEdit || isIaEdit
                  ? "💾 Enregistrer"
                  : "➕ Ajouter"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: "block", marginBottom: 12 }}>
      <span
        style={{
          display: "block",
          fontSize: 11,
          fontWeight: 700,
          color: "#444",
          marginBottom: 4,
          fontFamily: DS.fontBody,
        }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}

function _inputStyle() {
  return {
    width: "100%",
    padding: "8px 10px",
    border: `1px solid ${DS.border}`,
    borderRadius: 7,
    fontSize: 13,
    fontFamily: DS.fontBody,
    boxSizing: "border-box",
  };
}

function _hintStyle() {
  return {
    display: "block",
    marginTop: 2,
    fontSize: 10,
    color: "#888",
    fontFamily: DS.fontBody,
  };
}

function _btnPrimaryStyle(busy) {
  return {
    padding: "10px 16px",
    background: DS.navy,
    color: DS.white,
    border: "none",
    borderRadius: 7,
    cursor: busy ? "wait" : "pointer",
    fontFamily: DS.fontBody,
    fontSize: 13,
    fontWeight: 600,
    minHeight: 44,
    opacity: busy ? 0.6 : 1,
  };
}

function _btnSecondaryStyle() {
  return {
    padding: "10px 16px",
    background: DS.white,
    color: "#666",
    border: `1px solid ${DS.border}`,
    borderRadius: 7,
    cursor: "pointer",
    fontFamily: DS.fontBody,
    fontSize: 13,
    minHeight: 44,
  };
}

function _errorBoxStyle() {
  return {
    padding: 10,
    marginBottom: 4,
    background: "#fef2f2",
    border: `1px solid ${DS.charge}`,
    borderRadius: 7,
    color: DS.charge,
    fontSize: 12,
    fontFamily: DS.fontBody,
  };
}
